/**
 * Property Module Registry (Client-side)
 *
 * Defines available modules for the UI.
 */

import type { ModuleDefinition } from '../../../../../packages/entities/Property';

// Re-export types from entities for convenience
export type {
    AirBnBIntegrationConfig,
    BaseModuleConfig,
    CleaningConfig,
    HungarianDataReportingConfig,
    LockboxConfig,
    ModuleConfig,
    ModuleConfigField,
    ModuleDefinition,
    ModuleId,
    PropertyModules,
    SzamlazzConfig,
    TourismTaxConfig,
} from '../../../../../packages/entities/Property';

/**
 * Registry of all available modules
 */
export const MODULE_REGISTRY: ModuleDefinition[] = [
    {
        id: 'data-reporting/hungary',
        name: 'Hungarian Data Reporting',
        description:
            'Government-mandated reporting for properties located in Hungary',
        category: 'Compliance',
        requiredCountry: 'HU',
        configFields: [
            {
                key: 'reportingId',
                label: 'Reporting ID',
                type: 'text',
                required: true,
                placeholder: 'HU123456789',
                helpText: 'Official Hungarian government reporting identifier',
            },
            {
                key: 'contactEmail',
                label: 'Contact Email',
                type: 'email',
                required: true,
                placeholder: 'admin@property.com',
                helpText: 'Email address for compliance notifications',
            },
        ],
    },
    {
        id: 'integration/airbnb',
        name: 'AirBnB Integration',
        description:
            'Enable per-property AirBnB synchronization with custom credentials and sync preferences',
        category: 'Integration',
        configFields: [
            {
                key: 'listingId',
                label: 'Listing ID',
                type: 'text',
                required: true,
                placeholder: '12345678',
                helpText: 'AirBnB listing identifier from your property URL',
            },
            {
                key: 'cookie',
                label: 'Cookie',
                type: 'text',
                required: true,
                placeholder: 'AirBnB authentication cookie',
                helpText: 'Authentication cookie from AirBnB session',
            },
            {
                key: 'token',
                label: 'API Token',
                type: 'text',
                required: true,
                placeholder: 'AirBnB API token',
                helpText: 'API token for AirBnB requests',
            },
            {
                key: 'userId',
                label: 'User ID',
                type: 'text',
                required: false,
                placeholder: '181949830',
                helpText: 'AirBnB account ID (required for message sync)',
            },
            {
                key: 'syncBookings',
                label: 'Sync Bookings',
                type: 'boolean',
                required: false,
                helpText: 'Automatically import bookings from AirBnB',
            },
            {
                key: 'syncMessages',
                label: 'Sync Messages',
                type: 'boolean',
                required: false,
                helpText: 'Automatically sync message threads from AirBnB',
            },
        ],
    },
    {
        id: 'compliance/tourism-tax',
        name: 'Tourism Tax',
        description:
            'Enable tourism tax tracking and reporting for this property',
        category: 'Compliance',
        configFields: [],
    },
    {
        id: 'integration/lockbox',
        name: 'Lockbox Integration',
        description:
            'Enable property-scoped lockbox code management for guest access',
        category: 'Integration',
        configFields: [],
    },
    {
        id: 'integration/szamlazz',
        name: 'Számlázz.hu Integration',
        description:
            'Enable invoice generation through Számlázz.hu for this property',
        category: 'Integration',
        configFields: [],
    },
    {
        id: 'operations/cleaning',
        name: 'Cleaning Management',
        description:
            'Track cleaning records and payments for this property',
        category: 'Operations',
        configFields: [
            {
                key: 'fee',
                label: 'Cleaning Fee',
                type: 'number',
                required: true,
                placeholder: '125',
                helpText: 'Cleaning fee in property currency',
            },
            {
                key: 'avoidSundays',
                label: 'Avoid Sunday Cleanings',
                type: 'boolean',
                required: false,
                helpText: 'When enabled, suggests Monday for checkouts on Sunday if property is available',
            },
        ],
    },
];

/**
 * Get module definition by ID
 */
export function getModuleDefinition(
    moduleId: string
): ModuleDefinition | undefined {
    return MODULE_REGISTRY.find(m => m.id === moduleId);
}

/**
 * Get modules available for a specific country.
 * Filters out modules that have a requiredCountry that doesn't match.
 */
export function getAvailableModules(country: string): ModuleDefinition[] {
    return MODULE_REGISTRY.filter(
        module =>
            !module.requiredCountry || module.requiredCountry === country
    );
}

/**
 * Get count of enabled modules from property modules object
 */
export function getEnabledModuleCount(
    modules: Record<string, unknown> | undefined
): number {
    if (!modules) return 0;
    return Object.values(modules).filter(
        config =>
            typeof config === 'object' &&
            config !== null &&
            'enabled' in config &&
            config.enabled === true
    ).length;
}

/**
 * Get list of enabled module names
 */
export function getEnabledModuleNames(
    modules: Record<string, unknown> | undefined
): string[] {
    if (!modules) return [];
    return Object.entries(modules)
        .filter(
            ([_, config]) =>
                typeof config === 'object' &&
                config !== null &&
                'enabled' in config &&
                config.enabled === true
        )
        .map(([moduleId]) => {
            const definition = getModuleDefinition(moduleId);
            return definition?.name ?? moduleId;
        });
}
